import * as React from 'react';
import { Divider, Drawer } from '@mui/material';
import { Close, Home, Menu as MenuIcon } from '@mui/icons-material';
import { Button, IconButton } from 'components';
import { useNavigate } from 'react-router';
import { CATEGORIES_MENU_CONFIG } from 'constants/categoriesMenu';
import { APP_ROUTES } from 'constants/routes';
import './MobileCategoriesDrawer.styled.scss';
import { NativeAccordion } from 'components/atoms/NativeAccordion/NativeAccordion';
import { NativeAccordionSelectable } from 'components/atoms/NativeAccordion/NativeAccordionSelectable';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { useAppDispatch, useAppSelector } from 'store/store';
import { logoutUserAsyncThunk } from 'store/users/users.action';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const MobileCategoriesDrawer = () => {
  const localize = useLocalize();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: currentUser } = useAppSelector(
    (state) => state.users.currentUser
  );
  const { openModal } = useModals();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const handleHomeClick = () => {
    navigate(APP_ROUTES.HOME);
    handleClose();
  };
  const handleNavigation = (route: string) => {
    navigate(route);
    handleClose();
  };
  const handleSignInClick = () => {
    openModal(MODALS.LOGIN);
    handleClose();
  };
  const handleSignUpClick = () => {
    openModal(MODALS.REGISTRATION);
    handleClose();
  };
  const handleLogout = () => {
    dispatch(
      logoutUserAsyncThunk({
        onSuccess: () => {
          navigate(APP_ROUTES.HOME);
          handleClose();
        },
      })
    );
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        className="mobile-categories-drawer__trigger"
        color="primary"
        aria-label={localize('mobile.drawer.aria.open.menu') as string}
      >
        <MenuIcon fontSize="medium" />
      </IconButton>

      <Drawer
        anchor="left"
        open={isOpen}
        onClose={handleClose}
        className="mobile-categories-drawer"
        PaperProps={{
          className: 'mobile-categories-drawer__paper',
        }}
      >
        <div className="mobile-categories-drawer__content">
          <div className="mobile-categories-drawer__header">
            <div className="mobile-categories-drawer__title-wrap">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt={localize('header.brand.title') as string}
                className="mobile-categories-drawer__logo-image"
              />
            </div>
            <IconButton
              onClick={handleClose}
              className="mobile-categories-drawer__close-button"
              aria-label={localize('mobile.drawer.aria.close.menu') as string}
            >
              <Close />
            </IconButton>
          </div>

          <div className="mobile-categories-drawer__body">
            <div className="mobile-categories-drawer__body-scroll">
              {!currentUser ? (
                <div className="mobile-categories-drawer__auth">
                  <Button
                    color="primary"
                    variant="contained"
                    className="mobile-categories-drawer__auth__button"
                    onClick={handleSignInClick}
                  >
                    {localize('general.button.signin')}
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    className="mobile-categories-drawer__auth__button"
                    onClick={handleSignUpClick}
                  >
                    {localize('general.button.signup')}
                  </Button>
                </div>
              ) : null}
              {!currentUser ? <Divider /> : null}
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={handleHomeClick}
              >
                <Home sx={{ color: 'secondary.light' }} />
                <h6>{localize('mobile.drawer.home')}</h6>
              </div>
              <Divider />
              {CATEGORIES_MENU_CONFIG.map((category) => (
                <NativeAccordion
                  key={category.id}
                  title={localize(category.nameKey) as string}
                  titleIcon={category.icon as React.ReactNode}
                >
                  <NativeAccordionSelectable
                    title={
                      localize(
                        `nav.view.all.${category.id.replace(/-/g, '')}`
                      ) as string
                    }
                    onClick={() => {
                      navigate(`/${category.id}`);
                      handleClose();
                    }}
                  />
                  {category.subCategories.map((subCategory) => (
                    <NativeAccordionSelectable
                      key={subCategory.id}
                      title={localize(subCategory.nameKey) as string}
                      onClick={() => {
                        navigate(`/${category.id}/${subCategory.id}`);
                        handleClose();
                      }}
                    />
                  ))}
                </NativeAccordion>
              ))}
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() => handleNavigation(APP_ROUTES.ABOUT_US)}
              >
                <h6>{localize('mobile.drawer.about')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() => handleNavigation(APP_ROUTES.CONTACTS)}
              >
                <h6>{localize('mobile.drawer.contact')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() =>
                  handleNavigation(
                    `${APP_ROUTES.LEGAL_POLICIES}#privacy-policy`
                  )
                }
              >
                <h6>{localize('mobile.drawer.privacy')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() =>
                  handleNavigation(
                    `${APP_ROUTES.LEGAL_POLICIES}#terms-of-service`
                  )
                }
              >
                <h6>{localize('mobile.drawer.terms')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() =>
                  handleNavigation(
                    `${APP_ROUTES.LEGAL_POLICIES}#refund-return-policy`
                  )
                }
              >
                <h6>{localize('mobile.drawer.refund')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() =>
                  handleNavigation(
                    `${APP_ROUTES.LEGAL_POLICIES}#complaints-warranty-policy`
                  )
                }
              >
                <h6>{localize('mobile.drawer.complaints')}</h6>
              </div>
              <Divider />
              <div
                className="mobile-categories-drawer__body__accordionlike-button"
                role="button"
                onClick={() =>
                  handleNavigation(
                    `${APP_ROUTES.LEGAL_POLICIES}#shipping-policy`
                  )
                }
              >
                <h6>{localize('mobile.drawer.shipping')}</h6>
              </div>
            </div>
            {currentUser ? (
              <div className="mobile-categories-drawer__logout">
                <Button
                  color="primary"
                  variant="contained"
                  className="mobile-categories-drawer__auth__button"
                  onClick={handleLogout}
                  fullWidth
                >
                  {localize('general.button.logout')}
                </Button>
              </div>
            ) : null}
          </div>
          <div className="mobile-categories-drawer__footer">
            <p className="caption">{localize('mobile.drawer.footer.line1')}</p>
            <p className="caption">{localize('mobile.drawer.footer.rights')}</p>
          </div>
        </div>
      </Drawer>
    </>
  );
};
